import { AxiosResponse } from 'axios';
import { api } from '../../../../api/axiosInstance';
import { UrlRankRepository } from '../../Domain/Repositories/UrlRankRepository';
import {
  UrlRankJobAccepted,
  UrlRankLookupOutcome,
  UrlRankRequest,
  UrlRankResult,
  isUrlRankPendingStatus,
} from '../../Model/UrlRank';

function toStatusPath(jobIdOrStatusUrl: string): string {
  const raw = jobIdOrStatusUrl.trim();
  if (!raw) return '/utils/url-rank/';

  if (raw.startsWith('/')) {
    return raw.endsWith('/') ? raw : `${raw}/`;
  }

  if (raw.includes('/utils/url-rank/')) {
    try {
      const url = new URL(raw, 'https://placeholder.local');
      return url.pathname.endsWith('/') ? url.pathname : `${url.pathname}/`;
    } catch {
      // fall through
    }
  }

  const id = raw.replace(/\/+$/, '');
  return `/utils/url-rank/${id}/`;
}

export class UrlRankRepositoryImpl implements UrlRankRepository {
  async createUrlRankJob(request: UrlRankRequest): Promise<UrlRankLookupOutcome> {
    const body: Record<string, unknown> = {
      keyword: request.keyword,
      product_url: request.product_url,
    };
    if (request.force_refresh) {
      body.force_refresh = true;
    }

    const response: AxiosResponse<UrlRankResult & UrlRankJobAccepted> =
      await api.post('/utils/url-rank/', body);

    const data = response.data;
    const shouldPoll =
      response.status === 202 ||
      isUrlRankPendingStatus(data?.rank_check_status) ||
      Boolean(data?.job_id && !data?.completed_at);

    if (shouldPoll && data?.job_id) {
      return {
        kind: 'accepted',
        job: {
          job_id: data.job_id,
          rank_check_status:
            data.rank_check_status === 'running' ? 'running' : 'pending',
          rank: null,
          search_range: data.search_range,
          status_url: data.status_url || `/utils/url-rank/${data.job_id}/`,
        },
        seed: data,
      };
    }

    return { kind: 'stored', result: data };
  }

  async getUrlRankJob(jobIdOrStatusUrl: string): Promise<UrlRankResult> {
    const response: AxiosResponse<UrlRankResult> = await api.get(
      toStatusPath(jobIdOrStatusUrl),
    );
    return response.data;
  }
}
